import { ValidUtils } from "@dooboostore/core";
class ElementApply {
  constructor(el, config) {
    this.el = el;
    this.window = this.el.ownerDocument.defaultView ?? globalThis;
    this.document = this.el.ownerDocument;
    this.id = config?.id ?? el.id;
    this.variableWrap = config?.variableWrap ?? { start: "@", end: "@" };
  }
  stripWrap(script) {
    const start = this.variableWrap.start;
    const end = this.variableWrap.end;
    if (script) {
      return script.replace(/@(.+?)@/g, (_, v) => v);
    } else {
      return "";
    }
  }
  findVariables(script) {
    script = script ?? "";
    const start = this.variableWrap.start;
    const end = this.variableWrap.end;
    const variables = [];
    const regex = new RegExp(`${start}(.+?)${end}`, "g");
    let match;
    while ((match = regex.exec(script)) !== null) {
      variables.push(match[1]);
      const root = match[1].split(/[.[(]/)[0].trim();
      if (root && root !== match[1]) variables.push(root);
    }
    return variables;
  }
  getTarget(node, id) {
    if (node.nodeType === this.window.Node.COMMENT_NODE || node.data?.trim?.()?.startsWith(`ea:${this.id}:start:html`) || node.data?.trim?.()?.startsWith(`ea:${this.id}:start:text`)) {
      const fullStr = node.data?.trim?.();
      let [prefix, ...rest] = fullStr?.split(" ") ?? [];
      prefix = prefix.trim?.();
      const script = rest.join(" ");
      const [eaPrefix, eaId, startEnd, type] = prefix?.split(":") ?? [];
      const endPrefix = `${eaPrefix}:${eaId}:end:${type}`;
      let next = node.nextSibling;
      while (next) {
        if (next.nodeType === this.window.Node.COMMENT_NODE && next.data?.trim?.()?.startsWith(endPrefix)) {
          return { nodeType: "comment", type, start: node, end: next, script: script ?? "", variables: this.findVariables(script) };
        }
        next = next.nextSibling;
      }
    } else if (node.nodeType === this.window.Node.ELEMENT_NODE) {
      const aa = node.getAttributeNames().filter((name) => {
        return name.startsWith(`ea:${id}:attribute:`) || name.startsWith(`ea:${id}:event:`) || name.startsWith(`ea:${id}:property:`);
      });
      const element = node;
      const types = aa.map((attrName) => {
        const [eaPrefix, eaId, type, name] = attrName?.split(":") ?? [];
        if (eaPrefix === "ea" && eaId === id && (type === "attribute" || type === "event" || type === "property") && name) {
          const script = element.getAttribute(attrName);
          const a = {
            type,
            name,
            script,
            variables: this.findVariables(script)
          };
          return a;
        }
      }).filter(ValidUtils.isDefined);
      if (types?.length > 0) {
        const a = {
          nodeType: "element",
          node: element,
          types
        };
        return a;
      }
    }
  }
  findAllTarget() {
    const targetRoots = [this.el];
    if (this.el.shadowRoot) {
      targetRoots.push(this.el.shadowRoot);
    }
    const targets = [];
    targetRoots.forEach((el) => {
      const walker = this.document.createTreeWalker(el, this.window.NodeFilter.SHOW_ELEMENT | this.window.NodeFilter.SHOW_COMMENT, {
        acceptNode: (node) => {
          const target = this.getTarget(node, this.id);
          if (target) {
            targets.push(target);
            return this.window.NodeFilter.FILTER_ACCEPT;
          } else {
            return this.window.NodeFilter.FILTER_SKIP;
          }
        }
      });
      while (walker.nextNode()) {
      }
    });
    return targets;
  }
  replaceChildren(target, data) {
    let next = target.start.nextSibling;
    while (next && next !== target.end) {
      next.remove();
      next = target.start.nextSibling;
    }
    if (target.type === "html") {
      const t = this.document.createElement("template");
      t.innerHTML = data;
      const content = t.content;
      target.start.parentNode?.insertBefore(content, target.end);
    } else if (target.type === "text") {
      const content = this.document.createTextNode(data);
      target.start.parentNode?.insertBefore(content, target.end);
    }
  }
  apply(config) {
    const element = this.el;
    const id = this.id;
    const targets = this.findAllTarget();
    for (let it of targets) {
      const firstNode = it.nodeType === "comment" ? it.start : it.node;
      if (config?.target === "noInitialized" && firstNode.__ea_initialized) {
        continue;
      } else if (!firstNode.__ea_initialized) {
        firstNode.__ea_initialized = true;
      }
      const variables = /* @__PURE__ */ new Set();
      if (it.nodeType === "comment") {
        it.variables.forEach((v) => variables.add(v));
      } else {
        it.types.forEach((type) => {
          type.variables.forEach((v) => variables.add(v));
        });
      }
      const bind = config?.bind;
      const fromBind = (key) => bind != null && typeof key === "string" && !(key in (config?.context ?? {})) && ElementApply.isUserMember(bind, key);
      const context = new Proxy(config?.context ?? {}, {
        has(target, key) {
          return key in target || fromBind(key);
        },
        get(target, key) {
          const value = key in target ? target[key] : fromBind(key) ? bind[key] : void 0;
          return typeof value === "function" ? value.bind(bind ?? target) : value;
        }
      });
      if (!config?.targetVariableName || config?.targetVariableName && variables.has(config?.targetVariableName)) {
        if (it.nodeType === "comment") {
          const data = new Function("ctx", `
                  with(ctx) {
                    return function(){
                      return (${this.stripWrap(it.script)});
                    }
                  }
              `)(context).bind(it.start);
          if (config?.exclude?.html && it.type === "html") continue;
          if (config?.exclude?.text && it.type === "text") continue;
          this.replaceChildren(it, data());
        } else if (it.nodeType === "element") {
          for (const tit of it.types) {
            if (!config?.targetVariableName || config?.targetVariableName && tit.variables.find((v) => v === config?.targetVariableName)) {
              const data = new Function("ctx", `
                  with(ctx) {
                    return function(event){
                      return (${this.stripWrap(tit.script)});
                    }
                  }
              `)(context).bind(it.node);
              if (tit.type === "attribute" && !config?.exclude?.attribute) {
                it.node.setAttribute(tit.name, data());
              } else if (tit.type === "property" && !config?.exclude?.property) {
                it.node[tit.name] = data();
              } else if (tit.type === "event" && !config?.exclude?.event) {
                if (it.node[`__ea_event_${tit.name}-callback`]) {
                  it.node.removeEventListener(tit.name, it.node[`__ea_event_${tit.name}-callback`]);
                }
                if (data) {
                  it.node[`__ea_event_${tit.name}-callback`] = data;
                  it.node.addEventListener(tit.name, data);
                }
              }
            }
          }
        }
      }
    }
  }
  /** key 가 네이티브(DOM/내장) prototype 이 아니라 사용자 클래스나 인스턴스에 정의돼 있으면 true */
  static isUserMember(obj, key) {
    for (let p = obj; p; p = Object.getPrototypeOf(p)) {
      if (!Object.prototype.hasOwnProperty.call(p, key)) continue;
      const ctor = p === obj ? null : p.constructor;
      return !(typeof ctor === "function" && /\[native code\]/.test(Function.prototype.toString.call(ctor)));
    }
    return false;
  }
  removeAllEventListener() {
    const m = this.findAllTarget().filter((it) => it.nodeType === "element").flatMap((it) => it.types.map((sit) => ({ ...sit, node: it.node })).filter((it2) => it2.type === "event"));
    m.forEach((tit) => {
      if (tit.node[`__ea_event_${tit.name}-callback`]) {
        tit.node.removeEventListener(tit.name, tit.node[`__ea_event_${tit.name}-callback`]);
      }
    });
  }
  static htmlDataSet(id, script) {
    return { start: `ea:${id}:start:html ${script} `, end: `ea:${id}:end:html ` };
  }
  static html(id, script) {
    const s = ElementApply.htmlDataSet(id, script);
    return `<!--${s.start}--><!--${s.end}-->`;
  }
  static textDataSet(id, script) {
    return { start: `ea:${id}:start:text ${script} `, end: `ea:${id}:end:text ` };
  }
  static text(id, script) {
    const s = ElementApply.textDataSet(id, script);
    return `<!--${s.start}--><!--${s.end}-->`;
  }
  static attribute(id, name, script) {
    return `ea:${id}:attribute:${name}="${script}"`;
  }
  static event(id, name, script) {
    return `ea:${id}:event:${name}="${script}"`;
  }
  static property(id, name, script) {
    return `ea:${id}:property:${name}="${script}"`;
  }
  static attributeName(id, name) {
    return `ea:${id}:attribute:${name}`;
  }
  static eventName(id, name) {
    return `ea:${id}:event:${name}`;
  }
  static propertyName(id, name) {
    return `ea:${id}:property:${name}`;
  }
  /**
     <div>
     <!-- ea-dddd-start-html
     this.asd + this.zzz;
     ddddddddddddddddasdasd
     asdasdasd
     -->
     <!-- ea-dddd-end-html -->
  
     <div ea-dddd-attribute-value="ddddddddd @this@.zz+this.gg"   ea-dddd-event-click="wwww">
     asdasd
     </div>
     </div>
     */
}
export {
  ElementApply
};
//# sourceMappingURL=ElementApply.js.map
